import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Bot, FileText, ShoppingBag, Users } from "lucide-react";

const TABS = [
  {
    id: "customers",
    label: "For your customers",
    icon: ShoppingBag,
    eyebrow: "Self-service that gets somewhere",
    title: "Find it. Sort it. Get on with your day.",
    body: "Help customers find the policy or product detail they need, right when they need it. Give them a clear next step without hunting through your help center.",
  },
  {
    id: "team",
    label: "For your team",
    icon: Users,
    eyebrow: "Knowledge that stays in motion",
    title: "The idea. The decision. The context.",
    body: "Keep your team moving with answers pulled straight from your own documents and research — no more digging through folders for the one line that matters.",
  },
  {
    id: "ai",
    label: "For your AI",
    icon: Bot,
    eyebrow: "Context for every assistant",
    title: "Give your AI something to stand on.",
    body: "Feed your assistant relevant passages with source references, so answers come from your knowledge — not guesswork.",
  },
];

const SAMPLE_SOURCE =
  "We want you to feel good about what you ordered. If a colour, fit, or style is not quite right, you can start a return from the orders page in your account — have your order number ready so we can find the purchase. Unused items can be returned within 30 days of delivery. Keep the original tags attached and include all accessories in the packaging; final-sale and personalised products are excluded. If your order arrived damaged, contact support before sending it back.";

export const OutcomeTabs = () => {
  const [active, setActive] = useState("customers");
  const [sourceOpen, setSourceOpen] = useState(false);
  const tab = TABS.find((t) => t.id === active);

  return (
    <section
      data-testid="outcome-tabs"
      className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <span className="eyebrow">From a question to what’s next</span>
        <h2 className="mt-4 text-3xl font-medium tracking-tight text-[#f3f5f3] md:text-4xl lg:text-5xl">
          Your knowledge.
          <br />
          <span className="text-[#889296]">Put to work.</span>
        </h2>
        <p className="mt-4 max-w-xl text-sm text-[#889296] md:text-base">
          Help customers help themselves. Keep your team moving. Give your AI the
          context it needs.
        </p>
      </motion.div>

      <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            aria-pressed={active === t.id}
            data-testid={`outcome-tab-${t.id}`}
            onClick={() => setActive(t.id)}
            className={`group flex flex-col items-start gap-8 rounded-2xl border p-6 text-left transition-all duration-300 ${
              active === t.id
                ? "border-[#ff8a4c]/50 bg-[#111813] shadow-[0_12px_40px_rgba(255,138,76,0.08)]"
                : "border-[#233c4d]/30 bg-[#10191f] hover:border-[#233c4d]/70"
            }`}
          >
            <t.icon
              className={`h-7 w-7 transition-colors duration-300 ${
                active === t.id ? "text-[#ff8a4c]" : "text-[#bddcec]"
              }`}
              strokeWidth={1.6}
              aria-hidden="true"
            />
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#f3f5f3]">
              {t.label}
              <ArrowUpRight
                className="h-3.5 w-3.5 text-[#889296] transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                aria-hidden="true"
              />
            </span>
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab.id}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          data-testid="outcome-panel"
          className="mt-6 grid grid-cols-1 gap-8 rounded-3xl border border-[#233c4d]/30 bg-[#111813] p-8 md:grid-cols-2 md:p-12"
        >
          <div>
            <span className="eyebrow">{tab.eyebrow}</span>
            <h3 className="mt-3 text-xl font-medium text-[#f3f5f3] md:text-2xl">
              {tab.title}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-[#889296] md:text-base">
              {tab.body}
            </p>
          </div>

          <div className="rounded-2xl border border-[#233c4d]/40 bg-[#0b100d] p-6">
            <span className="eyebrow">The question</span>
            <h4 className="mt-2 text-lg font-medium text-[#f3f5f3]">
              Can I change my mind?
            </h4>
            <span className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-[#233c4d]/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-[#bddcec]">
              <FileText className="h-3 w-3" aria-hidden="true" /> Returns policy
            </span>
            <p className="mt-3 text-sm leading-relaxed text-[#c3cbce]">
              Unused items can be returned within 30 days of delivery.
            </p>
            <button
              type="button"
              aria-expanded={sourceOpen}
              data-testid="toggle-sample-source-btn"
              onClick={() => setSourceOpen((v) => !v)}
              className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#bce8d1] transition-colors duration-200 hover:text-[#edf2f4]"
            >
              {sourceOpen ? "Close sample source" : "Open sample source"}
              <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            {sourceOpen && (
              <div
                data-testid="sample-source-panel"
                className="mt-4 rounded-xl border border-[#233c4d]/30 bg-[#10191f] p-4"
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#889296]">
                  Original sample document
                </span>
                <p className="mt-2 text-xs leading-relaxed text-[#a7b1b5]">
                  {SAMPLE_SOURCE.split("Unused items can be returned within 30 days of delivery.")[0]}
                  <mark className="rounded bg-[#ff8a4c]/25 px-1 text-[#f3f5f3]">
                    Unused items can be returned within 30 days of delivery.
                  </mark>
                  {SAMPLE_SOURCE.split("Unused items can be returned within 30 days of delivery.")[1]}
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      <p className="mt-6 font-mono text-xs text-[#889296]">
        Illustrative examples. Explore a use case, then open its sample source.
      </p>
    </section>
  );
};
